import React, { useState } from 'react';
import { EmployeeStatusEnum, STATUS_CONFIG, TimeSlot } from '../types';
import { MultipleTimeSlotsEditor } from './MultipleTimeSlotsEditor';
import { calculateHoursFromTimeSlots, formatHours } from '../utils';

interface StatusCellProps {
    status: EmployeeStatusEnum;
    timeSlots: TimeSlot[];
    isToday?: boolean;
    onClick: () => void;
    onStatusChange?: (status: EmployeeStatusEnum, timeSlots: TimeSlot[]) => void;
}

interface StatusSelectorProps {
    currentStatus: EmployeeStatusEnum;
    onStatusChange: (status: EmployeeStatusEnum) => void;
    onEditTime: () => void;
    onClose: () => void;
    hasTimeSlots: boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({ 
    currentStatus, 
    onStatusChange, 
    onEditTime,
    onClose,
    hasTimeSlots
}) => {
    return (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]">
            <div className="p-2">
                <div className="text-sm font-medium text-gray-700 mb-2">
                    Changer le statut
                </div>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <button
                        key={key}
                        onClick={() => {
                            onStatusChange(key as EmployeeStatusEnum);
                            onClose();
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md mb-1 transition-colors
                            ${currentStatus === key 
                                ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                                : 'hover:bg-gray-50'
                            }`}
                    >
                        <span className={`inline-block w-3 h-3 rounded-full mr-2 ${config.color.split(' ')[0]}`}></span>
                        {config.label}
                    </button>
                ))}
                
                {(currentStatus === EmployeeStatusEnum.PRESENT || hasTimeSlots) && (
                    <>
                        <div className="border-t border-gray-100 my-2"></div>
                        <button
                            onClick={() => {
                                onEditTime();
                                onClose();
                            }}
                            className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-50 transition-colors text-blue-600"
                        >
                            <span className="inline-block w-3 h-3 mr-2">⏰</span>
                            Modifier les horaires
                        </button>
                    </>
                )}
            </div>
            <div className="border-t border-gray-100 p-2">
                <button
                    onClick={onClose}
                    className="w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                >
                    Annuler
                </button>
            </div>
        </div>
    );
};

export const StatusCell: React.FC<StatusCellProps> = ({ 
    status, 
    timeSlots, 
    isToday = false,
    onClick,
    onStatusChange 
}) => {
    const [showSelector, setShowSelector] = useState(false);
    const [showTimeEditor, setShowTimeEditor] = useState(false);
    const config = STATUS_CONFIG[status];

    const handleStatusChange = (newStatus: EmployeeStatusEnum) => {
        if (onStatusChange) {
            // Keep existing time slots if changing to PRESENT, otherwise clear them
            const newTimeSlots = newStatus === EmployeeStatusEnum.PRESENT ? timeSlots : [];
            onStatusChange(newStatus, newTimeSlots);
        }
        console.log('Status changed to:', newStatus);
    };

    const handleTimeChange = (newTimeSlots: TimeSlot[]) => {
        if (onStatusChange) {
            onStatusChange(status, newTimeSlots);
        }
        setShowTimeEditor(false);
        console.log('Time changed to:', newTimeSlots);
    };

    const formatTimeSlots = (slots: TimeSlot[]): string => {
        if (slots.length === 0) return '';
        if (slots.length === 1) {
            const slot = slots[0];
            return `${slot.startTime} - ${slot.endTime}`;
        }
        return `${slots.length} créneaux`;
    };

    const getTotalHours = (): number => {
        return calculateHoursFromTimeSlots(timeSlots);
    };

    const hasTimeSlots = timeSlots.length > 0;

    return (
        <div className="relative">
            <div
                onClick={() => {
                    setShowSelector(true);
                    onClick();
                }}
                className={`
                    w-full h-16 p-2 border-2 rounded-lg cursor-pointer transition-all duration-200
                    hover:shadow-md hover:scale-[1.02] active:scale-[0.98]
                    ${config.color}
                    ${isToday ? 'ring-2 ring-blue-400 ring-offset-1' : ''}
                    ${status === EmployeeStatusEnum.DEFAULT ? 'border-dashed' : ''}
                `}
            >
                <div className="flex flex-col h-full justify-center items-center text-center">
                    <div className="text-xs font-medium mb-1">
                        {config.label}
                    </div>
                    {hasTimeSlots && (status === EmployeeStatusEnum.PRESENT || status === EmployeeStatusEnum.DEFAULT) && (
                        <>
                            <div className="text-xs text-gray-600">
                                {formatTimeSlots(timeSlots)}
                            </div>
                            <div className="text-xs text-blue-600 font-medium">
                                {formatHours(getTotalHours())}
                            </div>
                        </>
                    )}
                    {status === EmployeeStatusEnum.DEFAULT && !hasTimeSlots && (
                        <div className="text-xs text-gray-400 italic">
                            Cliquer pour définir
                        </div>
                    )}
                </div>
            </div>

            {showSelector && (
                <>
                    <div 
                        className="fixed inset-0 z-5"
                        onClick={() => setShowSelector(false)}
                    />
                    <StatusSelector
                        currentStatus={status}
                        onStatusChange={handleStatusChange}
                        onEditTime={() => setShowTimeEditor(true)}
                        onClose={() => setShowSelector(false)}
                        hasTimeSlots={hasTimeSlots}
                    />
                </>
            )}

            {showTimeEditor && (
                <>
                    <div 
                        className="fixed inset-0 z-15"
                        onClick={() => setShowTimeEditor(false)}
                    />
                    <MultipleTimeSlotsEditor
                        initialTimeSlots={timeSlots}
                        onSave={handleTimeChange}
                        onClose={() => setShowTimeEditor(false)}
                    />
                </>
            )}
        </div>
    );
};